import React, { useEffect, useState } from 'react';
import { Trash2 } from 'react-feather';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Modal from '../../../Modal2/Modal';
import Avatar from '../../../avatar/Avatar';
import Button from '../../../button/Button';
import CommandBar from '../../../commandBar/CommandBar';
import List from '../../../list/List';
import { Paragraph, Small } from '../../../typography';

import GroupAccessLevelSelect from '../groupAccessLevelSelect/GroupAccessLevelSelect';
import GroupModalTitle from './GroupModalTitle';

import { messageGroupsService } from '../../../../newServices/corporateChat';
import { usersService } from '../../../../newServices/main';
import {
    GROUP_ACCESS_LEVEL_LABELS,
    accessLevelFromPermissions,
    getApiErrorMessage,
    isLastGroupManager,
    permissionsFromAccessLevel
} from '../../../../utils/internalChatGroups';

import internalChatToast from '../../utils/internalChatToast';
import useApplyGroupUpdate from '../../utils/useApplyGroupUpdate';

import './group-management.scss';

const GroupParticipantsModal = ({ show, group, canManage, onClose, onAddParticipants, onRemoveParticipant }) => {
    const intl = useIntl();
    const applyGroupUpdate = useApplyGroupUpdate();
    const loggedUserId = useSelector(state => state.auth.userData?.id);

    const [rolesByUserId, setRolesByUserId] = useState({});
    const [savingUserId, setSavingUserId] = useState(null);

    /** O papel de cadastro decide quem pode receber "Gerenciar grupo". Só quem gerencia
     *  precisa dele, então a busca só acontece nesse caso, ao abrir o modal. Sem os
     *  papéis, a opção fica escondida (menos para quem já gerencia).
     */
    useEffect(() => {
        if (!show || !canManage) return;

        let active = true;
        usersService.getAgentsCorporateAll()
            .then(res => {
                if (!active) return;
                const roles = {};
                ((res && res.data) || []).forEach(user => {
                    roles[user.id] = user.role;
                });
                setRolesByUserId(roles);
            })
            .catch(() => {});

        return () => {
            active = false;
        };
    }, [show, canManage]);

    /** A mesma rota que adiciona participantes troca o nível de quem já está. */
    const handleChangeLevel = async (participant, level) => {
        setSavingUserId(participant.userId);
        try {
            const res = await messageGroupsService.postMessageGroupParticipants(group.id, [{
                userId: participant.userId,
                permissions: permissionsFromAccessLevel(level)
            }]);
            applyGroupUpdate(res && res.data);
        } catch (error) {
            internalChatToast.error(
                getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível alterar a permissão. Tente novamente." })
            );
        } finally {
            setSavingUserId(null);
        }
    };

    const participants = Array.isArray(group?.participants) ? group.participants : null;

    return (
        <Modal
            show={show}
            toggle={onClose}
            position="center"
            size="md"
            className="group-participants-modal"
            onPressEnter={() => {}}
        >
            <Modal.Header>
                <GroupModalTitle
                    title={intl.formatMessage({ id: "Participantes do grupo" })}
                    groupName={group?.name} />
            </Modal.Header>
            <Modal.Content>
                {participants ?
                    <List>
                        <List.Group>
                            {participants.map(participant => {
                                const isLoggedUser = participant.userId === loggedUserId;
                                const lastManager = isLastGroupManager(group, participant.userId);
                                const level = accessLevelFromPermissions(participant.permissions);

                                return (
                                    <List.Item
                                        key={`group_participant_${participant.userId}`}
                                        className="group-participant-access-level"
                                        active={false}
                                        avatar={<Avatar name={participant.userName} size="lg" color="primary" />}
                                        title={isLoggedUser ?
                                            intl.formatMessage({ id: "{name} (você)" }, { name: participant.userName }) :
                                            participant.userName}
                                        subtitle={lastManager ? intl.formatMessage({ id: "Único gestor do grupo" }) : null}
                                        rightSideComponent={canManage ?
                                            <div className="group-participant-actions">
                                                <GroupAccessLevelSelect
                                                    id={`group-participant-level-${participant.userId}`}
                                                    role={rolesByUserId[participant.userId]}
                                                    value={level}
                                                    disabled={lastManager || savingUserId === participant.userId}
                                                    onChange={newLevel => handleChangeLevel(participant, newLevel)}
                                                />
                                                <CommandBar size="sm">
                                                    <CommandBar.Group>
                                                        <CommandBar.Item
                                                            color="danger"
                                                            active={!lastManager}
                                                            disabled={lastManager}
                                                            badge={null}
                                                            description={isLoggedUser ?
                                                                intl.formatMessage({ id: "Sair do grupo" }) :
                                                                intl.formatMessage({ id: "Remover {name}" }, { name: participant.userName })}
                                                            onClick={() => onRemoveParticipant(participant)}>
                                                            <Trash2 size={12} />
                                                        </CommandBar.Item>
                                                    </CommandBar.Group>
                                                </CommandBar>
                                            </div> :
                                            <Small className="group-participant-level">
                                                {intl.formatMessage({ id: GROUP_ACCESS_LEVEL_LABELS[level] })}
                                            </Small>
                                        }
                                    />
                                );
                            })}
                        </List.Group>
                    </List> :
                    <div className="p-1">
                        <Paragraph color="gray" light>
                            {intl.formatMessage({ id: "A lista de participantes deste grupo não está disponível no momento." })}
                        </Paragraph>
                    </div>
                }
            </Modal.Content>
            {canManage &&
                <Modal.Footer>
                    <div className="d-flex justify-content-end">
                        <Button.Primary dark onClick={onAddParticipants}>
                            {intl.formatMessage({ id: "Adicionar participantes" })}
                        </Button.Primary>
                    </div>
                </Modal.Footer>
            }
        </Modal>
    );
};

export default GroupParticipantsModal;
